import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip } from '@mui/material';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  SalaryPermissions,
  UpdateWorkShiftDto,
  WORK_SHIFT_CLOSE_DELAY_DAYS,
  WORK_SHIFT_MANUAL_CLOSE_REASON_MAX_LENGTH,
  WORK_SHIFT_PLAN_MAX,
  WORK_SHIFT_PLAN_MIN,
  WorkShiftDto,
} from '@sr/dto';
import { LoadingBanner } from 'shared/ui/loading-banner';
import { ErrorBanner } from 'shared/ui/error-banner';
import { useStateSelector } from 'shared/store';
import { formatDate, formatDateTime } from 'utils/date-format-helpers';
import { formatPerson } from 'utils/person-format-helpers';
import { extractNestJsErrorMessage } from 'shared/api/rtk-query';
import { useSnack } from 'shared/ui/snack';
import { workShiftsApi } from './api';
import { WorkShiftsFilter } from './work-shifts-filter';
import { periodFromSearchParams } from 'entities/period-picker/period.helpers';

const earliestClosableDate = (): string => {
  const date = new Date();
  date.setDate(date.getDate() - WORK_SHIFT_CLOSE_DELAY_DAYS);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

export const WorkShifts = () => {
  const storedPeriod = useStateSelector((state) => state.salaryPageSelectedPeriod.value);
  const [searchParams] = useSearchParams();
  const selectedPeriod = periodFromSearchParams(searchParams) || storedPeriod;
  const currentUser = useStateSelector((state) => state.auth.user);
  const { currentData: data = [], isFetching, error } = workShiftsApi.useGetWorkShiftsQuery({
    ...selectedPeriod,
    dateFrom: searchParams.get('dateFrom') || undefined,
    dateTo: searchParams.get('dateTo') || undefined,
    userId: searchParams.get('userId') ? Number(searchParams.get('userId')) : undefined,
  });
  const [closeShift] = workShiftsApi.useCloseWorkShiftMutation();
  const [selectedShift, setSelectedShift] = useState<WorkShiftDto | null>(null);
  const { showError, showSuccess } = useSnack();

  const canCloseAny = !!currentUser?.permissions.includes(SalaryPermissions.CLOSE_WORK_SHIFT_ANY);
  const earliestClosable = earliestClosableDate();

  const handleCloseShift = async (id: number) => {
    try {
      await closeShift(id).unwrap();
      showSuccess('План на день закрыт');
    } catch (e) {
      showError('Ошибка закрытия плана на день', e);
    }
  };

  return (
    <>
      <WorkShiftsFilter key={`${selectedPeriod.year}-${selectedPeriod.month}`} {...selectedPeriod} />
      {isFetching ? <LoadingBanner /> : error ? <ErrorBanner errorMessage={extractNestJsErrorMessage(error)} /> : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Дата</TableCell>
              <TableCell>Сотрудник</TableCell>
              <TableCell>Звонки</TableCell>
              <TableCell>КП</TableCell>
              <TableCell>Открыта</TableCell>
              <TableCell>Закрыта</TableCell>
              <TableCell>Закрыл</TableCell>
              <TableCell align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  За выбранный период рабочие смены не найдены.
                </TableCell>
              </TableRow>
            )}
            {data.map((shift) => {
              const isExpired = shift.date < earliestClosable;

              return (
                <TableRow key={shift.id}>
                  <TableCell>{formatDate(shift.date)}</TableCell>
                  <TableCell>{formatPerson(shift.user)}</TableCell>
                  <TableCell>{shift.callsFact} / {shift.callsPlan}</TableCell>
                  <TableCell>{shift.offersFact} / {shift.offersPlan}</TableCell>
                  <TableCell>{formatDateTime(shift.openedAt)}</TableCell>
                  <TableCell>{shift.closedAt ? formatDateTime(shift.closedAt) : '-'}</TableCell>
                  <TableCell>{shift.closedBy ? formatPerson(shift.closedBy) : '-'}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" onClick={() => setSelectedShift(shift)}>Редактировать</Button>
                      {!shift.closedAt && (
                        <Tooltip title={isExpired && !canCloseAny ? 'Смена просрочена - закрыть может только пользователь с пермиссией' : ''}>
                          <span>
                            <Button
                              size="small"
                              variant="contained"
                              disabled={isExpired && !canCloseAny}
                              onClick={() => handleCloseShift(shift.id)}
                            >
                            Закрыть
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      {selectedShift && <WorkShiftDialog shift={selectedShift} period={selectedPeriod} onClose={() => setSelectedShift(null)} />}
    </>
  );
};

const planError = (value: string): string => {
  const plan = Number(value);
  if (!value.trim() || !Number.isInteger(plan)) return 'Значение должно быть целым числом';
  if (plan < WORK_SHIFT_PLAN_MIN || plan > WORK_SHIFT_PLAN_MAX) return `Значение должно быть в диапазоне от ${WORK_SHIFT_PLAN_MIN} до ${WORK_SHIFT_PLAN_MAX}`;
  return '';
};

const WorkShiftDialog = ({ shift, period, onClose }: { shift: WorkShiftDto; period: { year: number; month: number }; onClose: () => void }) => {
  const [callsPlan, setCallsPlan] = useState(String(shift.callsPlan));
  const [offersPlan, setOffersPlan] = useState(String(shift.offersPlan));
  const [manualCloseReason, setManualCloseReason] = useState(shift.manualCloseReason || '');
  const [updateShift, { isLoading }] = workShiftsApi.useUpdateWorkShiftMutation();
  const { showError, showSuccess } = useSnack();

  const callsPlanError = planError(callsPlan);
  const offersPlanError = planError(offersPlan);
  const reasonError = manualCloseReason.length > WORK_SHIFT_MANUAL_CLOSE_REASON_MAX_LENGTH
    ? `Не более ${WORK_SHIFT_MANUAL_CLOSE_REASON_MAX_LENGTH} символов`
    : '';
  const hasErrors = !!callsPlanError || !!offersPlanError || !!reasonError;

  const handleSave = async () => {
    if (hasErrors) return;
    const data: UpdateWorkShiftDto = {
      callsPlan: Number(callsPlan),
      offersPlan: Number(offersPlan),
      manualCloseReason: manualCloseReason || null
    };
    try {
      await updateShift({ id: shift.id, data, period }).unwrap();
      showSuccess('План на день обновлен');
      onClose();
    } catch (e) {
      showError('Ошибка обновления плана на день', e);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>План на день</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="План звонков"
            type="number"
            inputProps={{ min: WORK_SHIFT_PLAN_MIN, max: WORK_SHIFT_PLAN_MAX, step: 1 }}
            value={callsPlan}
            onChange={(e) => setCallsPlan(e.target.value)}
            error={!!callsPlanError}
            helperText={callsPlanError}
            fullWidth
          />
          <TextField
            label="План КП"
            type="number"
            inputProps={{ min: WORK_SHIFT_PLAN_MIN, max: WORK_SHIFT_PLAN_MAX, step: 1 }}
            value={offersPlan}
            onChange={(e) => setOffersPlan(e.target.value)}
            error={!!offersPlanError}
            helperText={offersPlanError}
            fullWidth
          />
          <TextField
            label="Причина ручного закрытия"
            value={manualCloseReason}
            onChange={(e) => setManualCloseReason(e.target.value)}
            error={!!reasonError}
            helperText={reasonError}
            inputProps={{ maxLength: WORK_SHIFT_MANUAL_CLOSE_REASON_MAX_LENGTH }}
            fullWidth
            multiline
            minRows={3}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Отмена</Button>
        <Button variant="contained" onClick={handleSave} disabled={isLoading || hasErrors}>Сохранить</Button>
      </DialogActions>
    </Dialog>
  );
};
